import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Euro, Copy, CheckCircle, AlertCircle } from 'lucide-react';

const DonateEN = () => {
  const [donationAmount, setDonationAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  const donationAmounts = [10, 20, 50, 100, 250, 500];

  const handleDonationClick = (amount) => {
    setDonationAmount(amount);
    setCustomAmount('');
  };

  const handlePayPalClick = (e) => {
    if (donationAmount > 0) {
      // Try to open browser version of PayPal instead of app
      const paypalUrl = `https://www.paypal.com/paypalme/hayforhumanity/${donationAmount}EUR`;
      window.open(paypalUrl, '_blank', 'noopener,noreferrer');
      e.preventDefault();
    }
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value;
    // Remove leading zeros and only allow numbers
    const sanitized = value.replace(/^0+/, '') || '';
    setCustomAmount(sanitized);
    setDonationAmount(sanitized ? parseInt(sanitized) : 0);
  };

  const handleCustomAmountFocus = () => {
    // Clear preset selection when user clicks on custom input
    if (customAmount === '') {
      setDonationAmount(0);
    }
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <section id="donate" className="py-20 bg-warm-beige">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-6xl font-bold text-charcoal mb-6 font-heading">
              Your donation <span className="text-olive-green">helps</span>.
            </h2>
            <div className="w-24 h-1 bg-olive-green mx-auto mb-6"></div>
            <p className="text-xl text-charcoal/80 leading-relaxed">
              Every euro helps. Your donation reaches people who would be alone without our support. Together we bring aid to where it is most needed.
            </p>
          </motion.div>

          {/* Urgency Banner */}
          <motion.div
            className="bg-gradient-to-r from-red-500 to-red-600 text-white p-6 rounded-2xl mb-12 shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <AlertCircle className="w-8 h-8 flex-shrink-0" />
              <div>
                <h3 className="text-2xl font-bold mb-2 font-heading">Acute Emergency in Gaza</h3>
                <p className="text-white/95 leading-relaxed">
                  In winter 2026, hundreds of thousands of people in Gaza remain without prospects. Even after the agreed ceasefire, despair and suffering remain great.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* PayPal */}
            <motion.div
              className="bg-white p-8 rounded-3xl shadow-2xl border border-olive-green/10"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-olive-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Euro className="w-8 h-8 text-olive-green" />
                </div>
                <h3 className="text-3xl font-bold text-charcoal font-heading mb-2">
                  PayPal
                </h3>
              </div>

              {/* Amount Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-charcoal mb-3">
                  Select Amount
                </label>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {donationAmounts.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => handleDonationClick(amount)}
                      className={`py-3 px-4 rounded-xl font-bold transition-all ${
                        donationAmount === amount && customAmount === ''
                          ? 'bg-olive-green text-white shadow-lg scale-105'
                          : 'bg-cream text-charcoal hover:bg-olive-green/10'
                      }`}
                    >
                      {amount} €
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    onFocus={handleCustomAmountFocus}
                    placeholder="Enter custom amount"
                    className="w-full px-4 py-3 border-2 border-olive-green/20 rounded-xl focus:border-olive-green focus:outline-none text-charcoal font-medium"
                  />
                  <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-charcoal/50 font-medium">
                    €
                  </span>
                </div>
              </div>

              {/* PayPal Button */}
              <button
                onClick={handlePayPalClick}
                className="block w-full bg-gradient-to-r from-olive-green to-olive-dark hover:from-olive-dark hover:to-olive-green text-white py-4 rounded-xl font-bold text-lg text-center transition-all transform hover:scale-105 shadow-xl mb-4"
              >
                {donationAmount > 0 ? `Donate ${donationAmount} € for Gaza` : 'Donate Now'}
              </button>

              <div className="bg-cream p-4 rounded-xl">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-charcoal/70 leading-relaxed break-words font-semibold">
                      PayPal Account: <span className="font-semibold">info@hayforhumanity.de</span>
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard('info@hayforhumanity.de', 'PayPal')}
                    className="flex-shrink-0 p-2 hover:bg-olive-green/10 rounded-lg transition-colors group relative"
                    title="Copy"
                  >
                    {copiedField === 'PayPal' ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-charcoal/40 group-hover:text-olive-green" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-charcoal/60 mt-2 leading-relaxed font-semibold">
                  Please select "Friends & Family" to avoid fees.
                </p>
              </div>
            </motion.div>

            {/* Bank Transfer */}
            <motion.div
              className="bg-white p-8 rounded-3xl shadow-2xl border border-olive-green/10"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-olive-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Euro className="w-8 h-8 text-olive-green" />
                </div>
                <h3 className="text-3xl font-bold text-charcoal font-heading mb-2">
                  Bank Transfer
                </h3>
              </div>

              <div className="space-y-4">
                <div className="bg-cream p-4 rounded-xl">
                  <div className="space-y-3">
                    {/* Kontoinhaber */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-charcoal/60 mb-1">Account Holder</p>
                        <p className="text-charcoal font-medium break-words">
                          hay - humanity. action. you. eV
                        </p>
                      </div>
                      <button
                        onClick={() => copyToClipboard('hay - humanity. action. you. eV', 'Account Holder')}
                        className="flex-shrink-0 p-2 hover:bg-olive-green/10 rounded-lg transition-colors group relative"
                        title="Copy"
                      >
                        {copiedField === 'Account Holder' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Copy className="w-5 h-5 text-charcoal/40 group-hover:text-olive-green" />
                        )}
                      </button>
                    </div>

                    {/* IBAN */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-charcoal/60 mb-1">IBAN</p>
                        <p 
                          className="text-charcoal font-mono tracking-tight break-all"
                          style={{ fontSize: '15px', letterSpacing: '-0.3px' }}
                        >
                          DE91 8306 5408 0006 8776 48
                        </p>
                      </div>
                      <button
                        onClick={() => copyToClipboard('DE91830654080006877648', 'IBAN')}
                        className="flex-shrink-0 p-2 hover:bg-olive-green/10 rounded-lg transition-colors group relative"
                        title="Copy"
                      >
                        {copiedField === 'IBAN' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Copy className="w-5 h-5 text-charcoal/40 group-hover:text-olive-green" />
                        )}
                      </button>
                    </div>

                    {/* BIC */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-charcoal/60 mb-1">BIC</p>
                        <p className="text-charcoal font-mono tracking-tight text-sm sm:text-base break-all">
                          GENO DEF1 SLR
                        </p>
                      </div>
                      <button
                        onClick={() => copyToClipboard('GENODEF1SLR', 'BIC')}
                        className="flex-shrink-0 p-2 hover:bg-olive-green/10 rounded-lg transition-colors group relative"
                        title="Copy"
                      >
                        {copiedField === 'BIC' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Copy className="w-5 h-5 text-charcoal/40 group-hover:text-olive-green" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-olive-green/10 p-4 rounded-xl border-l-4 border-olive-green">
                  <p className="text-sm text-charcoal/70 leading-relaxed">
                    <strong>Donation Receipt:</strong> We are currently in the process of obtaining charitable status recognition. Donations can be claimed for tax purposes after successful recognition (not retroactively). Once charitable status is confirmed, we will be happy to issue corresponding donation certificates. We are happy to answer questions about this at:{' '}
                    <a 
                      href="mailto:info@hayforhumanity.de" 
                      className="text-olive-green hover:text-olive-dark underline font-medium transition-colors"
                    >
                      info@hayforhumanity.de
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Impact Message */}
          <motion.div
            className="mt-12 bg-gradient-to-r from-olive-green to-olive-dark text-white p-8 rounded-2xl shadow-xl text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Heart className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-4 font-heading">
              Every contribution counts. Every donation can save lives.
            </h3>
            <p className="text-lg text-white/90 leading-relaxed max-w-2xl mx-auto">
              Whether 10, 20 or 100 euros: Your support transforms compassion into real help. Follow us on{' '}
              <a 
                href="https://whatsapp.com/channel/0029Vb7J4Wr8qIzrTYt6rl0O" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:text-white font-semibold transition-colors"
              >
                WhatsApp
              </a>
              {' '}and{' '}
              <a 
                href="https://www.instagram.com/hayforhumanity?igsh=MXVjaHB3Ync4MjgxZA==" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:text-white font-semibold transition-colors"
              >
                Instagram
              </a>
              {' '}for updates.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default DonateEN;